import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { formatDateTimeMY } from '../utils/dateUtils';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Search,
  ShoppingCart,
  MessageSquare,
  GitBranch,
  Send,
  XCircle,
  SkipForward,
  Clock,
  ChevronRight,
  User,
  Phone,
  Loader2,
  ArrowLeft,
} from 'lucide-react';

const EVENT_CONFIG = {
  order:            { icon: ShoppingCart,  color: 'bg-blue-500',   label: 'Order' },
  campaign_message: { icon: Send,          color: 'bg-purple-500', label: 'Campaign' },
  flow_enrolled:    { icon: GitBranch,     color: 'bg-emerald-500',label: 'Flow' },
  flow_message:     { icon: MessageSquare, color: 'bg-teal-500',   label: 'Flow Message' },
};

const STATUS_BADGE = {
  sent:    'bg-green-100 text-green-700',
  failed:  'bg-red-100 text-red-700',
  skipped: 'bg-gray-100 text-gray-600',
  pending: 'bg-yellow-100 text-yellow-700',
  active:  'bg-green-100 text-green-700',
  stopped: 'bg-red-100 text-red-700',
  completed:'bg-blue-100 text-blue-700',
};

function formatDate(iso) {
  return formatDateTimeMY(iso);
}

const ClientHistoryPage = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [loadingTimeline, setLoadingTimeline] = useState(false);
  const [totalEvents, setTotalEvents] = useState(0);

  const handleSearch = useCallback(async () => {
    if (!query.trim()) return;
    setSearching(true);
    setSelectedClient(null);
    setTimeline([]);
    try {
      const res = await api.get('/clients', { params: { search: query.trim(), limit: 10, page: 1 } });
      setSearchResults(res.data.clients || []);
    } catch {
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, [query]);

  const handleSelectClient = async (client) => {
    setSelectedClient(client);
    setLoadingTimeline(true);
    try {
      const res = await api.get(`/clients/${client.id}/timeline`);
      setTimeline(res.data.events || []);
      setTotalEvents(res.data.total || 0);
    } catch {
      setTimeline([]);
    } finally {
      setLoadingTimeline(false);
    }
  };

  const handleBack = () => {
    setSelectedClient(null);
    setTimeline([]);
  };

  return (
    <div className="space-y-6" data-testid="client-history-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Client History</h1>
        <p className="text-sm text-gray-500 mt-1">Search any client by name or phone to see their full activity timeline</p>
      </div>

      {/* Search Bar */}
      <Card className="p-4">
        <div className="flex gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <Input
              data-testid="history-search-input"
              placeholder="Search by client name or phone number..."
              className="pl-10"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
          </div>
          <Button onClick={handleSearch} disabled={searching || !query.trim()} data-testid="history-search-btn">
            {searching ? <Loader2 className="animate-spin mr-2" size={16} /> : <Search size={16} className="mr-2" />}
            Search
          </Button>
        </div>
      </Card>

      {/* Search Results (show when no client selected) */}
      {!selectedClient && searchResults !== null && (
        <div className="space-y-2">
          {searchResults.length === 0 ? (
            <Card className="p-8 text-center text-gray-500">
              <User size={40} className="mx-auto mb-3 text-gray-300" />
              <p className="font-medium">No clients found</p>
              <p className="text-sm mt-1">Try a different name or phone number</p>
            </Card>
          ) : (
            <>
              <p className="text-sm text-gray-500">{searchResults.length} client{searchResults.length !== 1 ? 's' : ''} found</p>
              {searchResults.map((c) => (
                <Card
                  key={c.id}
                  className="p-4 cursor-pointer hover:border-[#4F46E5] hover:shadow-md transition-all group"
                  onClick={() => handleSelectClient(c)}
                  data-testid={`history-client-card-${c.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-[#4F46E5]/10 flex items-center justify-center text-[#4F46E5] font-semibold">
                        {c.name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">{c.name}</p>
                        <div className="flex items-center gap-3 text-sm text-gray-500 mt-0.5">
                          <span className="flex items-center gap-1"><Phone size={12} />{c.phone}</span>
                          <span>{c.orders || 0} orders</span>
                          <span>RM {(c.total_spend || 0).toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                    <ChevronRight size={20} className="text-gray-300 group-hover:text-[#4F46E5] transition-colors" />
                  </div>
                </Card>
              ))}
            </>
          )}
        </div>
      )}

      {/* Timeline View (show when client selected) */}
      {selectedClient && (
        <div className="space-y-4">
          {/* Client Header */}
          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Button variant="outline" size="sm" onClick={handleBack} data-testid="history-back-btn">
                  <ArrowLeft size={16} className="mr-1" /> Back
                </Button>
                <div className="w-10 h-10 rounded-full bg-[#4F46E5] flex items-center justify-center text-white font-semibold">
                  {selectedClient.name?.charAt(0)?.toUpperCase() || '?'}
                </div>
                <div>
                  <p className="font-bold text-gray-900 text-lg">{selectedClient.name}</p>
                  <p className="text-sm text-gray-500">{selectedClient.phone} {selectedClient.email ? `| ${selectedClient.email}` : ''}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right text-sm">
                  <p className="text-gray-500">{selectedClient.orders || 0} orders</p>
                  <p className="font-semibold text-gray-800">RM {(selectedClient.total_spend || 0).toFixed(2)}</p>
                </div>
                <Button variant="outline" size="sm" onClick={() => navigate(`/clients/${selectedClient.id}`)} data-testid="view-client-detail-btn">
                  View Profile <ChevronRight size={14} className="ml-1" />
                </Button>
              </div>
            </div>
          </Card>

          {/* Timeline */}
          {loadingTimeline ? (
            <Card className="p-12 text-center">
              <Loader2 className="animate-spin mx-auto mb-3 text-[#4F46E5]" size={32} />
              <p className="text-gray-500">Loading timeline...</p>
            </Card>
          ) : timeline.length === 0 ? (
            <Card className="p-12 text-center text-gray-500">
              <Clock size={40} className="mx-auto mb-3 text-gray-300" />
              <p className="font-medium">No activity yet</p>
              <p className="text-sm mt-1">Orders, campaigns, and flows will appear here</p>
            </Card>
          ) : (
            <div>
              <p className="text-sm text-gray-500 mb-3">{totalEvents} event{totalEvents !== 1 ? 's' : ''}</p>
              <div className="relative">
                {/* Vertical line */}
                <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-gray-200" />
                <div className="space-y-0">
                  {timeline.map((event, idx) => {
                    const cfg = EVENT_CONFIG[event.type] || EVENT_CONFIG.order;
                    const Icon = cfg.icon;
                    const metaStatus = event.meta?.status || event.meta?.entry_status;
                    return (
                      <div key={idx} className="relative flex gap-4 pb-6" data-testid={`timeline-event-${idx}`}>
                        {/* Dot */}
                        <div className={`relative z-10 w-10 h-10 rounded-full ${cfg.color} flex items-center justify-center text-white flex-shrink-0 shadow-sm`}>
                          <Icon size={16} />
                        </div>
                        {/* Content */}
                        <Card className="flex-1 p-3 hover:shadow-sm transition-shadow">
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="font-medium text-gray-900 text-sm">{event.title}</p>
                                {metaStatus && (
                                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_BADGE[metaStatus] || 'bg-gray-100 text-gray-600'}`}>
                                    {metaStatus}
                                  </span>
                                )}
                              </div>
                              {event.subtitle && <p className="text-xs text-gray-500 mt-0.5">{event.subtitle}</p>}
                              {event.detail && <p className="text-xs text-gray-400 mt-0.5">{event.detail}</p>}
                            </div>
                            <span className="text-xs text-gray-400 whitespace-nowrap ml-3 mt-0.5">{formatDate(event.date)}</span>
                          </div>
                        </Card>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ClientHistoryPage;
